import compact from "lodash/compact";
import find from "lodash/find";
import sortBy from "lodash/sortBy";
import { ComparePage } from "PFApp/activities/compare/compare_page";
import { ReassignActionContextProvider } from "PFApp/booking/parts/overview/booking_module_calendar/context/reassign_action_context_provider";
import { PageWrapper } from "PFComponents/page_wrapper";
import { useActivity } from "PFCore/hooks/queries/activity";
import { useInfiniteActivityWatchers } from "PFCore/hooks/queries/activity/use_activity_watchers";
import { useActivityRankedShortlists } from "PFCore/hooks/queries/shortlists/use_activity_ranked_shortlists";
import { useActivityShortlists } from "PFCore/hooks/queries/shortlists/use_activity_shortlists";
import { useActivitySuspendedShortlists } from "PFCore/hooks/queries/shortlists/use_activity_suspended_shortlists";
import { fetchInvites } from "PFCore/services/invites/fetch_invites";
import { fetchProfile } from "PFCore/services/profile";
import { Activity, Profile, Shortlist } from "PFTypes";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";

import { ComparableProfile } from "./details/parts/use_cell_data";

const fetchData = async (id: number, profileIds: number[] | null, shortlists: Shortlist[]) => {
  const shortlistsWithInvite = await fetchInvites({ targetType: "Task", targetId: id }).then(
    ({ entries: invites }) =>
      sortBy(
        (shortlists || []).map((shortlist) => ({
          ...shortlist,
          invite: find(invites, { profileId: shortlist.profile.id })
        })),
        ["id"]
      )
  );

  const profilesWithFullData = await Promise.all(
    (profileIds || shortlists.map(({ profile }) => profile.id)).map((profileId) => fetchProfile(profileId))
  );

  return {
    shortlistsWithInvite,
    profilesWithFullData
  };
};

export const CompareController = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, tab, ids, type } = useParams<{
    id: string;
    tab: "details" | "availability";
    ids?: string;
    type?: string;
  }>();
  const activityId = Number(id);

  const initProfileIds = ids ? compact(ids.split(",")).map((id) => parseInt(id, 10)) : null;

  const [profileIds, setProfilesIds] = useState<number[] | null>(initProfileIds);
  const [shortlists, setShortlists] = useState<Shortlist[] | null>(null);
  const [rankLoaded, setRankLoaded] = useState(false);
  const [profilesWithFullData, setProfilesWithFullData] = useState<Profile[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const prevPath = useRef(location.state?.prevPath);

  const isInterestedType = type === "interested";
  const { watchers } = useInfiniteActivityWatchers(
    { id: activityId } as Activity,
    1000,
    {},
    {},
    { enabled: isInterestedType }
  );
  const watchersWithShortlists = watchers.map(
    (watcher) => (shortlists || []).find(({ profile }) => profile.id === watcher.profile.id) || watcher
  );

  const exitToShortlist = () => navigate(`/activities/${id}/shortlist`);
  const exitToDetails = () => navigate(`/activities/${id}/details`);
  const exitToInterested = () => navigate(`/activities/${id}/interested`);

  const updateData = (shortlists: Shortlist[]) => {
    const activeProfileIds = profileIds
      ? profileIds.filter((profileId) => shortlists.find(({ profile }) => profile.id === profileId))
      : profileIds;

    return fetchData(activityId, activeProfileIds, shortlists)
      .then(({ shortlistsWithInvite, profilesWithFullData }) => {
        if (shortlistsWithInvite.length === 0 && !isInterestedType) {
          exitToShortlist();
        }
        setShortlists(shortlistsWithInvite);
        setProfilesWithFullData(profilesWithFullData);
        setIsLoaded(true);
        setRankLoaded(false);
      })
      .catch(exitToDetails);
  };

  const { data: activity, isSuccess: isActivitySuccess } = useActivity(activityId);
  const { data: shortlistsResponse, dataUpdatedAt: shortlistsDataUpdatedAt } = useActivityShortlists(
    activityId,
    {},
    {
      enabled: isActivitySuccess
    }
  );

  useEffect(() => {
    if (!shortlistsResponse) {
      return;
    }
    updateData(shortlistsResponse.entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortlistsDataUpdatedAt]);

  const { data: rankedShortlistsResponse, dataUpdatedAt: rankedShortlistsDataUpdatedAt } =
    useActivityRankedShortlists({
      activityId,
      options: {
        enabled: !!shortlists && shortlists.length > 0 && !rankLoaded
      }
    });

  useEffect(() => {
    if (!rankedShortlistsResponse) {
      return;
    }
    const rankedShortlists = shortlists?.map((shortlist) => {
      const rank = rankedShortlistsResponse.entries.find(
        ({ profile }) => profile.id === shortlist.profile.id
      );

      return {
        ...(rank || {}),
        ...shortlist,
        profile: {
          ...(rank?.profile || {}),
          ...shortlist.profile
        }
      };
    });
    setRankLoaded(true);
    setShortlists((rankedShortlists as Shortlist[]) ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankedShortlistsDataUpdatedAt]);

  const { data: suspendedShortlistsData } = useActivitySuspendedShortlists(activityId);

  const getPath = (newTab?: string, ids?: number[] | string | null) => {
    newTab = newTab === "availability" ? newTab : "details";

    let rest = "";
    if (ids === "__RETAIN__") {
      rest = `/${profileIds ? profileIds.join(",") : ""}`;
    } else if (ids) {
      rest = `/${(ids as number[]).join(",")}`;
    }
    if (type) {
      rest = rest.concat(`/${type}`);
    }

    return `/activities/${id}/compare/${newTab}${rest}`;
  };

  const changeTab = (newTab: string) =>
    navigate({ pathname: getPath(newTab, profileIds) }, { state: { prevPath: prevPath.current } });

  useEffect(() => {
    changeTab(tab as string);
  }, []); // if invalid tab goes to details

  const removeProfileId = (id: number) => {
    const ids = profileIds ? profileIds : (shortlists as Shortlist[]).map(({ profile: { id } }) => id);
    setProfilesIds(ids.filter((pid) => pid !== id));
  };

  useEffect(() => {
    // null is valid (means all of the ids), any array is a subset
    if (profileIds && profileIds.length === 0) {
      isInterestedType ? exitToInterested() : exitToShortlist();
    } else {
      navigate({ pathname: getPath(tab, profileIds) }, { state: { prevPath: prevPath.current } });
    }
  }, [profileIds]);

  const filteredProfiles = useMemo(() => {
    const suspendedShortlists = suspendedShortlistsData?.entries || [];
    const shortlistsWithSuspended = [...(shortlists ?? []), ...(suspendedShortlists ?? [])];
    const profiles = (isInterestedType ? watchersWithShortlists : shortlistsWithSuspended) || [];

    return profiles.filter(({ profile: { id } }) => !profileIds || profileIds.includes(id));
  }, [shortlists, suspendedShortlistsData, profileIds, isInterestedType, watchersWithShortlists]);

  const comparableProfiles = filteredProfiles.map((item) => {
    const profileFullData = profilesWithFullData.find(({ id }) => id === item.profile.id);
    return {
      ...item,
      isAvailable: !!profileFullData,
      availabilityMatch: (item.scores as { availabilityScore?: number } | undefined)?.availabilityScore,
      profile: { ...item.profile, ...(profileFullData ?? {}) }
    };
  }) as ComparableProfile[];

  return (
    <ReassignActionContextProvider>
      <PageWrapper isLoading={!isLoaded}>
        <ComparePage
          tab={tab || "details"}
          changeTab={changeTab}
          comparableProfiles={comparableProfiles}
          removeProfileId={removeProfileId}
          activity={activity!}
        />
      </PageWrapper>
    </ReassignActionContextProvider>
  );
};
